import { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from './services/api';
import { useQuery } from '@tanstack/react-query';
import Layout from './components/layout/Layout';
import PortalLayout from './components/layout/PortalLayout';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Appointments from './pages/Appointments';
import Notes from './pages/Notes';
import NoteEditor from './pages/NoteEditor';
import Billing from './pages/Billing';
import InvoiceForm from './pages/InvoiceForm';
import InvoiceDetail from './pages/InvoiceDetail';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import AppointmentConfirm from './pages/AppointmentConfirm';
import ContactReply from './pages/ContactReply';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalAppointments from './pages/portal/PortalAppointments';
import PortalInvoices from './pages/portal/PortalInvoices';
import PortalInvoiceDetail from './pages/portal/PortalInvoiceDetail';
import PortalDocuments from './pages/portal/PortalDocuments';
import PortalSchedule from './pages/portal/PortalSchedule';
import Insurance from './pages/Insurance';
import InsuranceClaimDetail from './pages/InsuranceClaimDetail';
import InsuranceClaimForm from './pages/InsuranceClaimForm';
import ERAList from './pages/ERAList';
import ERADetail from './pages/ERADetail';
import Reports from './pages/Reports';
import IntakeForms from './pages/IntakeForms';
import IntakeFormDetail from './pages/IntakeFormDetail';
import IntakeFormResponseViewer from './pages/IntakeFormResponseViewer';
import PortalIntakeForms from './pages/portal/PortalIntakeForms';
import PortalIntakeFormFill from './pages/portal/PortalIntakeFormFill';
import Messages from './pages/Messages';
import PortalMessages from './pages/portal/PortalMessages';
import Tasks from './pages/Tasks';
import TreatmentPlanEditor from './pages/TreatmentPlanEditor';
import Onboarding from './pages/Onboarding';
import PlatformAdmin from './pages/PlatformAdmin';
import BAAWall from './components/common/BAAWall';
import authStore from './store/authStore';
import { getPractice } from './services/settingsService';
import themeStore from './store/themeStore';
import useInactivityLogout from './hooks/useInactivityLogout';
import InactivityWarningModal from './components/common/InactivityWarningModal';
import Landing from './pages/Landing';
import ForProviders from './pages/ForProviders';
import ForClients from './pages/ForClients';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import BAAPage from './pages/legal/BAAPage';
import HIPAANotice from './pages/legal/HIPAANotice';

function EmailVerificationWall({ email, onLogout }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      await api.post('/api/v1/auth/resend-verification');
      setSent(true);
      toast.success('Verification email sent — check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send verification email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
      <div className="card max-w-md w-full text-center space-y-4">
        <h1 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">Check your email</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          We sent a verification link to <strong>{email}</strong>. Please verify your email to continue.
        </p>
        <p className="text-xs text-neutral-500 dark:text-neutral-500">
          Once verified, refresh this page.
        </p>
        <button
          onClick={handleResend}
          disabled={sending || sent}
          className="btn btn-primary w-full"
        >
          {sending ? 'Sending…' : sent ? 'Email sent!' : 'Resend verification email'}
        </button>
        <button onClick={onLogout} className="btn btn-secondary w-full">Sign out</button>
      </div>
    </div>
  );
}

export default function App() {
  const { isAuthenticated, isLoading, user, logout, loadUser } = authStore();
  const { initTheme } = themeStore();
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  const handleWarn   = useCallback(() => setShowInactivityWarning(true), []);
  const handleLogout = useCallback(() => { setShowInactivityWarning(false); logout(); }, [logout]);
  const handleStay   = useCallback(() => setShowInactivityWarning(false), []);

  useInactivityLogout({ enabled: isAuthenticated, onWarn: handleWarn, onLogout: handleLogout });

  useEffect(() => {
    loadUser();
    initTheme();
  }, [loadUser, initTheme]);

  // Listen for forced logout events from the axios interceptor
  useEffect(() => {
    window.addEventListener('auth:logout', logout);
    return () => window.removeEventListener('auth:logout', logout);
  }, [logout]);

  // Must be called unconditionally (Rules of Hooks) — enabled flag controls when it actually runs
  const { data: practiceData, isLoading: isPracticeLoading } = useQuery({
    queryKey: ['settings', 'practice'],
    queryFn: getPractice,
    enabled: isAuthenticated && user?.role !== 'client' && !user?.isPlatformAdmin && user?.emailVerified !== false,
    retry: false,
  });

  const inactivityModal = (
    <InactivityWarningModal
      isOpen={showInactivityWarning}
      onStayLoggedIn={handleStay}
      onLogout={handleLogout}
    />
  );

  if (isLoading || (isPracticeLoading && isAuthenticated && user?.role !== 'client' && !user?.isPlatformAdmin && user?.emailVerified !== false)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/for-providers" element={<ForProviders />} />
        <Route path="/for-clients" element={<ForClients />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        {/* Public — no login required */}
        <Route path="/confirm" element={<AppointmentConfirm />} />
        <Route path="/contact-reply" element={<ContactReply />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/baa" element={<BAAPage />} />
        <Route path="/hipaa-notice" element={<HIPAANotice />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const needsEmailVerification =
    isAuthenticated &&
    !user?.isPlatformAdmin &&
    user?.role !== 'client' &&
    user?.emailVerified === false;

  const needsOnboarding =
    isAuthenticated &&
    !user?.isPlatformAdmin &&
    user?.role !== 'client' &&
    !needsEmailVerification &&
    !practiceData?.practice?.onboardingCompletedAt;

  const needsBAA =
    isAuthenticated &&
    !user?.isPlatformAdmin &&
    user?.role !== 'client' &&
    !!practiceData?.practice?.onboardingCompletedAt &&
    !practiceData?.practice?.baaAcceptedAt;

  if (user?.isPlatformAdmin) {
    return (
      <>
        {inactivityModal}
        <Routes>
          <Route path="/platform-admin" element={<PlatformAdmin />} />
          <Route path="*" element={<Navigate to="/platform-admin" replace />} />
        </Routes>
      </>
    );
  }

  if (user?.role === 'client') {
    return (
      <>
        {inactivityModal}
        <Routes>
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalDashboard />} />
            <Route path="appointments" element={<PortalAppointments />} />
            <Route path="invoices" element={<PortalInvoices />} />
            <Route path="invoices/:id" element={<PortalInvoiceDetail />} />
            <Route path="documents" element={<PortalDocuments />} />
            <Route path="schedule" element={<PortalSchedule />} />
            <Route path="intake-forms" element={<PortalIntakeForms />} />
            <Route path="intake-forms/:responseId" element={<PortalIntakeFormFill />} />
            <Route path="messages" element={<PortalMessages />} />
          </Route>
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      </>
    );
  }

  if (needsEmailVerification) {
    return (
      <Routes>
        <Route path="/verify-email" element={<VerifyEmail onSuccess={loadUser} />} />
        <Route path="*" element={<EmailVerificationWall email={user?.email} onLogout={logout} />} />
      </Routes>
    );
  }

  if (needsOnboarding) {
    return (
      <>
        {inactivityModal}
        <Routes>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="*" element={<Navigate to="/onboarding" replace />} />
        </Routes>
      </>
    );
  }

  if (needsBAA) {
    return (
      <>
        {inactivityModal}
        <BAAWall onAccepted={() => {}} />
      </>
    );
  }

  return (
    <>
      {inactivityModal}
      <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="notes" element={<Notes />} />
        <Route path="notes/new" element={<NoteEditor />} />
        <Route path="notes/:id" element={<NoteEditor />} />
        <Route path="billing" element={<Billing />} />
        <Route path="billing/new" element={<InvoiceForm />} />
        <Route path="billing/:id" element={<InvoiceDetail />} />
        <Route path="insurance" element={<Insurance />} />
        <Route path="insurance/new" element={<InsuranceClaimForm />} />
        <Route path="insurance/eras" element={<ERAList />} />
        <Route path="insurance/eras/:id" element={<ERADetail />} />
        <Route path="insurance/:id" element={<InsuranceClaimDetail />} />
        <Route path="insurance/:id/edit" element={<InsuranceClaimForm />} />
        <Route path="intake-forms" element={<IntakeForms />} />
        <Route path="intake-forms/responses/:responseId" element={<IntakeFormResponseViewer />} />
        <Route path="intake-forms/:id" element={<IntakeFormDetail />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="treatment-plans/new" element={<TreatmentPlanEditor />} />
        <Route path="treatment-plans/:id" element={<TreatmentPlanEditor />} />
        <Route path="messages" element={<Messages />} />
        <Route path="reports" element={<Reports />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="verify-email" element={<VerifyEmail />} />
      <Route path="confirm" element={<AppointmentConfirm />} />
      <Route path="contact-reply" element={<ContactReply />} />
      <Route path="privacy" element={<PrivacyPolicy />} />
      <Route path="terms" element={<TermsOfService />} />
      <Route path="baa" element={<BAAPage />} />
      <Route path="hipaa-notice" element={<HIPAANotice />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}
